/* Giveaway limit: one free giveaway item per customer per campaign, swap on
 * re-pick.
 *
 * Business rules (see GIVEAWAY-LIMIT.md):
 *  - At most ONE line whose product carries the `giveaway` tag, at quantity 1.
 *  - Adding a different giveaway SWAPS: the just-added line is kept and every
 *    other giveaway line is removed. Re-adding the same one clamps it to 1.
 *  - A signed-in customer who already CLAIMED this campaign (their customer
 *    tags include the theme setting giveaway_campaign_tag, which Shopify Flow
 *    adds after the order) gets no free item at all: every giveaway line is
 *    removed, with the claimedRemoved notice. The Liquid add buttons for such
 *    a customer are already aria-disabled, so this path only runs for adds
 *    that bypass them (other add paths, a stale page, another tab). Guests and
 *    logged-out shoppers are never "claimed" here; the checkout rule reads the
 *    buyer's customer record (guest email included) at checkout.
 *  - Non-giveaway lines (bundles, regular products) are never touched.
 *  - Checkout is backstopped server-side by the "Giveaway limit" checkout rule
 *    (Cart & Checkout Validation Function), so everything here FAILS OPEN: if
 *    the state section is missing, unparsable, or a request fails, the cart is
 *    left alone and the theme behaves exactly as it did before.
 *
 * Where the cart state comes from: sections/giveaway-cart-state.liquid is
 * requested through the Section Rendering API. It is appended to every
 * `cart:grouped-sections` collection, so each theme cart mutation (add,
 * change, update) returns it alongside the drawer / main-cart sections.
 * It renders <script data-giveaway-state>{"claimed":false,"lines":[...]}</script>:
 * `claimed` is computed in Liquid from the signed-in customer and the campaign
 * tag setting (false when logged out or the setting is blank; a missing or
 * non-boolean value counts as false), and `lines` has one entry per giveaway
 * line in cart order. A line's `title` is the line item title (product +
 * variant), used as-is in the notices.
 *
 * Cart order (live probe): cart.items is NEWEST-CREATED first, and re-adding a
 * variant that already has a line bumps its quantity without moving it.
 *
 * Three enforcement paths, all serialized through ONE promise queue so two
 * quick-adds never interleave their removals:
 *  1. afterAdd(addResponse) — called by ProductForm (assets/theme.js hook)
 *     between the /cart/add response and its own POST /cart + cartUpdate
 *     publish. Reconciles with one /cart/update.js call and hands ProductForm
 *     the reconciled sections, so the drawer opens on the final cart. The
 *     line the shopper just added is the one kept (claimed customer: removed
 *     along with any other giveaway line, and the notice names it). If the
 *     cart had to be re-read first (overlapping adds, retry), ProductForm gets
 *     freshly rendered sections, never ones from before another reconcile.
 *  2. Safety net — a cartUpdate subscriber that catches violations from other
 *     add paths (products-bundle, quick-order-list, ...) and publishes its own
 *     cartUpdate after reconciling. It has no "just added" line to go on, so
 *     it keeps lines[0]: the NEWEST-CREATED giveaway line (not necessarily
 *     the most recently re-added one). Claimed customer: removes them all.
 *  3. Page load / bfcache restore / `cart:refresh` (Easy Bundles SDK) — same
 *     as the safety net, from the Liquid-rendered or freshly fetched state.
 *     On `cart:refresh` it first waits for the cart drawer's own refresh to
 *     land (see waitForDrawerRefresh).
 *
 * Status pills (selected / swap / idle, or claimed on every one for a claimed
 * customer) are re-applied after the theme swaps in product cards (facet /
 * sort re-renders, quick view).
 *
 * Config: window.FoxTheme.giveawayConfig (snippets/giveaway-head.liquid),
 * only output when settings.giveaway_limit_enabled is on: { section, state
 * (Liquid-rendered { claimed, lines }), strings: { limitOne, swapped,
 * keptOne, claimedRemoved } }.
 *
 * Public API: window.FoxTheme.giveaway = { afterAdd, getState, _planReconcile }
 * (getState() -> { claimed, lines } copy or null; _planReconcile(lines,
 * keepKey, claimed?) is the pure planner, exposed for tests.)
 */
(function () {
  'use strict';

  var FT = window.FoxTheme;
  var CFG = FT && FT.giveawayConfig;
  if (!CFG || !FT.pubsub || !FT.pubsub.PUB_SUB_EVENTS || !FT.routes || !FT.utils) return;

  var SECTION_ID = CFG.section || 'giveaway-cart-state';
  var STR = CFG.strings || {};
  var CART_UPDATE = FT.pubsub.PUB_SUB_EVENTS.cartUpdate;
  var LOG_PREFIX = '[giveaway-limit]';

  // Every request we make is abandoned after this long, so afterAdd (which
  // ProductForm awaits with its button spinning) always resolves.
  var FETCH_TIMEOUT_MS = 10000;

  // Hard ceiling on automatic (non-afterAdd) reconciles per page view, on top
  // of the per-signature guard, in case another app keeps re-adding lines.
  var MAX_AUTO_RECONCILES = 3;

  // Longest we hold a `cart:refresh` reconcile back while waiting for the cart
  // drawer to swap in its own refreshed markup.
  var DRAWER_REFRESH_TIMEOUT_MS = 5000;

  // CartDrawer's own refresh fetch has no timeout. After we stop waiting we
  // keep watching this long; a replacement that lands late is stale, so the
  // drawer is re-rendered once more from a fresh cart.
  var DRAWER_LATE_WATCH_MS = 30000;

  /* ================= state ================= */

  var lastState = null; // most recent valid { claimed, lines } we have seen
  var pendingNotice = null; // text shown in the cart on the next re-render
  var inFlight = 0; // afterAdd calls not yet resolved + running auto reconcile
  var attempted = new Set(); // violating-state signatures already auto-reconciled
  var autoCount = 0;
  var halted = false; // an automatic reconcile failed: stop trying this page view

  /* ================= utilities ================= */

  function noop() {}

  // "[old]"-style placeholders. Function replacers so titles containing "$&"
  // etc. are inserted literally.
  function fill(template, vars) {
    var out = String(template || '');
    Object.keys(vars).forEach(function (name) {
      out = out.replace('[' + name + ']', function () {
        return vars[name] == null ? '' : String(vars[name]);
      });
    });
    return out;
  }

  // fetch + JSON with a timeout. Resolves { ok, status, data } or null on a
  // network error / abort. Never rejects.
  async function fetchJSON(url, options) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    try {
      var res = await fetch(url, Object.assign({}, options, controller ? { signal: controller.signal } : null));
      var data = null;
      try { data = await res.json(); } catch (e) { data = null; }
      return { ok: res.ok, status: res.status, data: data };
    } catch (e) {
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // Polite live region, created on first use (pattern from bundle-builder.js).
  var announceTimer = null;
  function announce(text) {
    if (!text || !document.body) return;
    var region = document.getElementById('GiveawayLive');
    var delay = 60;
    if (!region) {
      region = document.createElement('div');
      region.id = 'GiveawayLive';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
      // A brand-new region needs a beat before screen readers watch it.
      delay = 250;
    }
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () { region.textContent = text; }, delay);
  }

  /* ================= cart state ================= */

  // Validates a raw { claimed, lines: [...] } object. Any malformed line
  // invalidates the whole state (fail open rather than act on partial data).
  // `claimed` is true only when it is literally true: a missing or garbled
  // flag (e.g. an older state snippet) means "not claimed", which removes
  // nothing.
  function normalizeState(data) {
    if (!data || !Array.isArray(data.lines)) return null;
    var lines = [];
    for (var i = 0; i < data.lines.length; i++) {
      var line = data.lines[i];
      if (!line || typeof line.key !== 'string' || !line.key) return null;
      var quantity = Number(line.quantity);
      if (!Number.isFinite(quantity)) return null;
      lines.push({
        key: line.key,
        variant_id: line.variant_id,
        product_id: line.product_id,
        quantity: quantity,
        title: typeof line.title === 'string' ? line.title : '',
      });
    }
    return { claimed: data.claimed === true, lines: lines };
  }

  // sections object from a cart response ({ "giveaway-cart-state": html|null })
  // -> { claimed, lines } or null.
  function parseState(sections) {
    try {
      var html = sections && sections[SECTION_ID];
      if (typeof html !== 'string' || !html) return null;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector('script[data-giveaway-state]');
      if (!node) return null;
      return normalizeState(JSON.parse(node.textContent));
    } catch (e) {
      return null;
    }
  }

  // GET the state section on its own (after a failed update, page restore,
  // cart:refresh). Resolves { claimed, lines } or null. No JSON Accept header: /cart
  // answers `Accept: application/json` with the cart object (ProductForm
  // relies on that), which would bypass the Section Rendering response.
  async function fetchState() {
    var res = await fetchJSON(FT.routes.cart_url + '?sections=' + encodeURIComponent(SECTION_ID), {
      cache: 'no-store',
    });
    if (!res || !res.ok || !res.data) return null;
    return parseState(res.data);
  }

  /**
   * Pure planner. lines = giveaway lines in cart order; keepKey = line key to
   * keep (falls back to lines[0], the newest-created line, when null / not
   * found); claimed = the customer already claimed this campaign (optional,
   * falsy when omitted).
   * Returns null when the cart already complies, else
   * { updates: { [key]: qty }, kept: line, removed: [lines] }.
   * Claimed: nothing may stay, so every line is removed (in cart order) and
   * `kept` is null; keepKey is ignored.
   */
  function planReconcile(lines, keepKey, claimed) {
    if (!Array.isArray(lines) || !lines.length) return null;
    if (claimed === true) {
      var removeAll = {};
      lines.forEach(function (line) { removeAll[line.key] = 0; });
      return { updates: removeAll, kept: null, removed: lines.slice() };
    }
    var kept = null;
    if (keepKey != null) {
      kept = lines.find(function (line) { return line.key === keepKey; }) || null;
    }
    if (!kept) kept = lines[0];

    var updates = {};
    var removed = [];
    lines.forEach(function (line) {
      if (line.key === kept.key) return;
      updates[line.key] = 0;
      removed.push(line);
    });
    if (Number(kept.quantity) !== 1) updates[kept.key] = 1;

    if (!Object.keys(updates).length) return null;
    return { updates: updates, kept: kept, removed: removed };
  }

  function violates(state) {
    return !!state && planReconcile(state.lines, null, state.claimed) !== null;
  }

  // The claimed flag is part of the signature: an unclaimed state that was
  // already attempted must not block the (different) claimed plan for the
  // same lines.
  function signature(state) {
    return (state.claimed === true ? 'claimed:' : '') +
      JSON.stringify(state.lines.map(function (line) { return line.key + ':' + line.quantity; }));
  }

  // Claimed plans name the line the notice is about: the just-added line when
  // it is among the removed ones, else the first removed line.
  function claimedTitle(plan, keepKey) {
    var line = keepKey != null
      ? plan.removed.find(function (removed) { return removed.key === keepKey; })
      : null;
    return (line || plan.removed[0]).title;
  }

  // Line items the add call created or incremented, in request order.
  function addedItems(addResponse) {
    if (addResponse.key) return [addResponse];
    return Array.isArray(addResponse.items) ? addResponse.items : [];
  }

  // The LAST added item that is a giveaway line in `lines`. Matched by key
  // first; if no key matches, by variant id, so a retry that re-plans from a
  // fresh state never flips the shopper's pick back to the old one. (Keys were
  // stable in live probes; the variant fallback guards against re-keying, e.g.
  // a discount re-allocation, which could not be probed.)
  function resolveKeepKey(lines, items) {
    var i, match;
    for (i = items.length - 1; i >= 0; i--) {
      if (!items[i]) continue;
      match = lines.find(function (line) { return line.key === items[i].key; });
      if (match) return match.key;
    }
    for (i = items.length - 1; i >= 0; i--) {
      if (!items[i] || items[i].variant_id == null) continue;
      match = lines.find(function (line) { return String(line.variant_id) === String(items[i].variant_id); });
      if (match) return match.key;
    }
    return null;
  }

  function setState(state) {
    lastState = state;
    renderStatus(state);
  }

  // Status pills on cards / PDP / quick view. JS only flips the attribute; the
  // CSS in giveaway-head.liquid shows the matching child. A claimed customer
  // sees `claimed` on every giveaway product, whatever the cart holds.
  function renderStatus(state) {
    var productIds = new Set(state.lines.map(function (line) { return String(line.product_id); }));
    var hasAny = state.lines.length > 0;
    document.querySelectorAll('[data-giveaway-status-for]').forEach(function (el) {
      var value = state.claimed === true
        ? 'claimed'
        : productIds.has(String(el.getAttribute('data-giveaway-status-for')))
          ? 'selected'
          : hasAny ? 'swap' : 'idle';
      if (el.getAttribute('data-giveaway-status') !== value) el.setAttribute('data-giveaway-status', value);
    });
    syncAddButtons(state.claimed === true);
  }

  // Giveaway add buttons (card quick add, PDP / quick view, sticky bar) are the
  // buttons described by a status line (aria-describedby="GiveawayStatus-…").
  // Liquid renders aria-disabled="true" + data-giveaway-claimed on them for a
  // customer who already claimed; keep that in sync when the claim changes
  // within a page view (bfcache restore after signing in or out, Flow tagging
  // mid-session) and after a variant change, where ProductForm.resetFormState
  // strips aria-disabled. A button mid-submit (btn--loading) is left alone, and
  // aria-disabled is only removed from buttons this feature disabled.
  function syncAddButtons(claimed) {
    try {
      document.querySelectorAll('[aria-describedby^="GiveawayStatus-"]').forEach(function (button) {
        if (button.classList && button.classList.contains('btn--loading')) return;
        if (claimed) {
          if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
          if (!button.hasAttribute('data-giveaway-claimed')) button.setAttribute('data-giveaway-claimed', '');
        } else if (button.hasAttribute('data-giveaway-claimed')) {
          button.removeAttribute('data-giveaway-claimed');
          button.removeAttribute('aria-disabled');
        }
      });
    } catch (e) {
      /* cosmetic sync only: submits of claimed customers are still reconciled */
    }
  }

  /* ================= cart requests ================= */

  // Same section collection every theme cart call uses, so the response can be
  // published straight to CartItems (it throws on a missing section).
  function collectSectionIds() {
    var ids = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: ids } })
    );
    if (ids.indexOf(SECTION_ID) === -1) ids.push(SECTION_ID);
    return ids;
  }

  // One /cart/update.js call for the whole plan (removals + clamp together, by
  // line key). Resolves the full cart JSON (+ sections) or null on failure.
  // postUpdate({}) changes nothing and just returns the current cart with
  // freshly rendered sections (live probe: 200, full cart + sections).
  async function postUpdate(updates) {
    var res = await fetchJSON(FT.routes.cart_update_url + '.js', Object.assign(FT.utils.fetchConfig(), {
      body: JSON.stringify({
        updates: updates,
        sections: collectSectionIds(),
        sections_url: window.location.pathname,
      }),
    }));
    if (!res || !res.ok || !res.data || res.data.status) return null;
    if (updatesIgnored(updates, res.data)) return null;
    return res.data;
  }

  // Stale keys: per live probe, a key whose line (variant) is gone makes
  // Shopify reject the WHOLE batch with a 422 ("updates parameter is
  // invalid"; none of the other updates are applied), which postUpdate already
  // treats as a failure via !res.ok, so callers re-read and retry.
  // This check is defensive only: Shopify never returned a 2xx that skipped a
  // key. If one ever does (a line we set to 0 still present, or a clamped line
  // at a quantity other than 1), it also counts as a failure, so we never
  // announce a change that didn't happen. Lines the plan didn't name (a
  // concurrent add) are ignored.
  function updatesIgnored(updates, cart) {
    var after = parseState(cart.sections);
    if (!after) return false;
    return after.lines.some(function (line) {
      return Object.prototype.hasOwnProperty.call(updates, line.key) && line.quantity !== updates[line.key];
    });
  }

  // Publish a reconciled cart to CartItems & co. CartItems moves focus into
  // the cart after every update (meant for the shopper's own edits); a
  // background reconcile puts it back where it was when that element survived
  // the re-render. CartItems also points the theme focus trap at the cart it
  // re-rendered; on /cart with the drawer closed that would trap Tab inside
  // the main cart, so release it unless the drawer is open.
  function publishCart(cart) {
    var before = document.activeElement;
    try {
      FT.pubsub.publish(CART_UPDATE, { cart: cart });
    } catch (e) {
      console.warn(LOG_PREFIX, 'cart re-render failed', e);
    }
    try {
      if (FT.a11y && typeof FT.a11y.removeTrapFocus === 'function' && !document.querySelector('cart-drawer[open]')) {
        FT.a11y.removeTrapFocus();
      }
    } catch (e) {
      /* focus housekeeping only */
    }
    var after = document.activeElement;
    if (after === before) return;
    if (!before || before === document.body) {
      if (after && after !== document.body && typeof after.blur === 'function') after.blur();
    } else if (before.isConnected && typeof before.focus === 'function') {
      before.focus({ preventScroll: true });
    }
  }

  /* ================= serial queue ================= */

  var queue = Promise.resolve();
  var activeTasks = 0;

  // Runs task after everything already queued. The chain itself never
  // rejects, so one failing step can't stall the steps behind it.
  function enqueue(task) {
    activeTasks++;
    var run = queue.then(function () { return task(); });
    queue = run.then(done, done);
    function done() { activeTasks--; }
    return run;
  }

  /* ================= 1. after an add (ProductForm hook) ================= */

  /**
   * Called by ProductForm with the /cart/add response. Resolves the response
   * to continue with: the original one, or a copy whose `sections` show the
   * reconciled (or freshly re-read) cart. Never throws or rejects.
   */
  function afterAdd(addResponse) {
    if (!addResponse || typeof addResponse !== 'object') return Promise.resolve(addResponse);
    try {
      // If other reconciles are queued/running, this add response may describe
      // a cart they are about to change; re-read the state before planning.
      var queueWasBusy = activeTasks > 0;
      inFlight++;
      return enqueue(function () { return afterAddTask(addResponse, queueWasBusy); })
        .catch(function (error) {
          console.warn(LOG_PREFIX, 'afterAdd failed; leaving the cart as added', error);
          return addResponse;
        })
        .then(function (result) {
          inFlight--;
          return result || addResponse;
        });
    } catch (error) {
      return Promise.resolve(addResponse);
    }
  }

  async function afterAddTask(addResponse, queueWasBusy) {
    pendingNotice = null;

    var state = parseState(addResponse.sections);
    if (!state) return addResponse; // state section unavailable: fail open
    if (queueWasBusy) {
      var latest = await fetchState();
      if (latest) state = latest;
    }
    setState(state);

    // For a claimed customer keepKey only identifies the just-added line for
    // the notice; the plan removes every giveaway line regardless.
    var items = addedItems(addResponse);
    var keepKey = resolveKeepKey(state.lines, items);
    var plan = planReconcile(state.lines, keepKey, state.claimed);
    if (!plan) return finishWithoutPlan(addResponse, keepKey, queueWasBusy);

    var cart = await postUpdate(plan.updates);
    if (!cart) {
      // Most likely a stale line key (Shopify 422s the whole batch). Re-read
      // the cart and try exactly once more.
      var fresh = await fetchState();
      if (!fresh) {
        console.warn(LOG_PREFIX, 'cart update failed and state could not be re-read; leaving cart as added');
        return addResponse;
      }
      setState(fresh);
      keepKey = resolveKeepKey(fresh.lines, items);
      plan = planReconcile(fresh.lines, keepKey, fresh.claimed);
      if (!plan) return finishWithoutPlan(addResponse, keepKey, true);
      cart = await postUpdate(plan.updates);
      if (!cart) {
        console.warn(LOG_PREFIX, 'cart update failed twice; leaving cart as added');
        return addResponse;
      }
    }

    var next = parseState(cart.sections);
    if (next) setState(next);

    // Claimed (plan.kept is null): every giveaway line is gone, including the
    // one just added. Otherwise, no keepKey = the add wasn't a giveaway but
    // the cart already broke the rule; the first line was kept, so "swapped"
    // (and quantity 1) would be wrong for what the shopper just added.
    if (!plan.kept) {
      pendingNotice = fill(STR.claimedRemoved, { item: claimedTitle(plan, keepKey) }) || null;
    } else if (!keepKey) {
      pendingNotice = fill(STR.keptOne, { kept: plan.kept.title }) || null;
    } else {
      pendingNotice = plan.removed.length
        ? fill(STR.swapped, { old: plan.removed[0].title, new: plan.kept.title })
        : STR.limitOne || null;
    }
    announce(pendingNotice);

    // ProductForm renders these sections, then POSTs /cart for fresh cart
    // JSON and publishes cartUpdate itself. Quantity 1 only when the
    // shopper's pick was kept; a claimed removal leaves the add response's
    // quantity as it was.
    var reconciled = { sections: cart.sections || addResponse.sections };
    if (keepKey && plan.kept) reconciled.quantity = 1;
    return Object.assign({}, addResponse, reconciled);
  }

  // afterAddTask found nothing to reconcile. `reread` = the plan came from a
  // re-read of the cart rather than from addResponse.sections (the queue was
  // busy, or the first update failed): those sections may then predate another
  // reconcile that changed the cart (with overlapping quick-adds it can even
  // have removed this add's line), so hand ProductForm fresh ones instead.
  // A giveaway add still gets the limit notice: shown in the cart on the next
  // re-render and announced here, since the inserted notice isn't a live
  // region (e.g. a shopper's first free pick). (For a claimed customer a plan
  // exists whenever any giveaway line does, so keepKey is always null here.)
  async function finishWithoutPlan(addResponse, keepKey, reread) {
    var result = reread ? await withFreshSections(addResponse, keepKey) : addResponse;
    if (keepKey) {
      pendingNotice = STR.limitOne || null;
      announce(pendingNotice);
    }
    return result;
  }

  // ProductForm renders the sections we return, and the drawer's remove /
  // quantity controls address lines by position (data-index), so stale markup
  // would act on the wrong line. An empty update returns the current cart with
  // freshly rendered sections for every grouped section id. If that fails,
  // fall back to the add response (the safety net re-checks ProductForm's
  // publish).
  async function withFreshSections(addResponse, keepKey) {
    var cart = await postUpdate({});
    if (!cart || !cart.sections) return addResponse;
    var state = parseState(cart.sections);
    if (state) setState(state);
    var fresh = { sections: cart.sections };
    // keepKey was found in the re-read with nothing to fix, so its line is at 1.
    if (keepKey) fresh.quantity = 1;
    return Object.assign({}, addResponse, fresh);
  }

  /* ================= 2 + 3. automatic reconcile ================= */

  // Queue a reconcile of lastState unless the loop guards say no. It keeps
  // lines[0], the NEWEST-CREATED giveaway line (cart.items is newest-first and
  // re-adding an existing line doesn't move it, so a re-add through a
  // non-ProductForm path can lose to a newer, different pick), or removes
  // every giveaway line for a claimed customer. Deferred so the publish that
  // revealed the violation, and CartItems' re-render, finish first.
  function scheduleAutoReconcile() {
    if (halted || autoCount >= MAX_AUTO_RECONCILES) return;
    if (!violates(lastState) || attempted.has(signature(lastState))) return;
    setTimeout(function () {
      enqueue(autoReconcileTask).catch(function (error) {
        halted = true;
        console.warn(LOG_PREFIX, 'automatic reconcile failed', error);
      });
    }, 0);
  }

  // Keeps lines[0] (newest-created giveaway line), or removes all of them for
  // a claimed customer; see scheduleAutoReconcile.
  async function autoReconcileTask() {
    // An afterAdd still owes ProductForm's publish, which re-checks the cart.
    if (halted || inFlight > 0 || autoCount >= MAX_AUTO_RECONCILES) return;
    var state = lastState;
    if (!violates(state)) return;
    var sig = signature(state);
    if (attempted.has(sig)) return;

    attempted.add(sig);
    autoCount++;
    pendingNotice = null;

    var plan = planReconcile(state.lines, null, state.claimed);
    var cart = null;
    var alreadyFixed = false;
    var current = null;
    inFlight++;
    try {
      cart = await postUpdate(plan.updates);
      if (!cart) {
        var fresh = await fetchState();
        if (fresh) {
          setState(fresh);
          plan = planReconcile(fresh.lines, null, fresh.claimed);
          if (!plan) {
            // Someone else already fixed the cart. Fetch it with its sections
            // so the stale render that triggered this reconcile can be
            // replaced (published below).
            alreadyFixed = true;
            current = await postUpdate({});
          } else {
            var freshSig = signature(fresh);
            if (!attempted.has(freshSig)) {
              attempted.add(freshSig);
              cart = await postUpdate(plan.updates);
            }
          }
        }
      }
    } finally {
      inFlight--;
    }

    if (alreadyFixed) {
      // The publish that revealed the violation rendered a cart that no longer
      // exists (e.g. a late ProductForm publish with pre-reconcile sections);
      // its line-position controls would act on the wrong lines. Republish the
      // current cart. This runs after inFlight dropped, so the usual guards
      // apply to it: the state is compliant, so it schedules nothing. No
      // notice, since this reconcile changed nothing.
      if (current) {
        var parsed = parseState(current.sections);
        if (parsed) setState(parsed);
        publishCart(current);
      }
      return;
    }

    if (!cart) {
      halted = true;
      console.warn(LOG_PREFIX, 'automatic reconcile failed; not retrying on this page');
      return;
    }

    var next = parseState(cart.sections);
    if (next) setState(next);

    pendingNotice = plan.kept
      ? fill(STR.keptOne, { kept: plan.kept.title })
      : fill(STR.claimedRemoved, { item: claimedTitle(plan, null) });
    announce(pendingNotice);
    publishCart(cart);
  }

  // Re-read the cart (bfcache restore, cart:refresh) and enforce, once
  // `waitFor` (a promise, or nothing) settles.
  function refreshFromServer(waitFor) {
    function run() {
      enqueue(async function () {
        var state = await fetchState();
        if (!state) return;
        setState(state);
        scheduleAutoReconcile();
      }).catch(noop);
    }
    Promise.resolve(waitFor).then(run, run);
  }

  // CartDrawer.onCartRefresh (assets/cart.js) answers `cart:refresh` too: it
  // GETs root_url?section_id=<drawer> and then replaces the innerHTML of
  // #CartDrawer-<data-section-id> (the element itself persists) with markup
  // rendered when ITS request was made. If our reconcile published first, that
  // late replacement would bring back the pre-reconcile lines, with
  // data-index remove links that now hit the wrong line (a paid item), and
  // wipe the notice. So before reading the cart we wait for that replacement
  // (first childList mutation) or DRAWER_REFRESH_TIMEOUT_MS; our publish, and
  // the notice it inserts, then land on top of the drawer's refresh.
  // (childList without subtree: CartItems' re-renders and the notice only
  // touch descendants inside #CartDrawerBody-<id>, so they can't end the wait.)
  // Must be called synchronously from the cart:refresh listener so the
  // observer is attached before the drawer's fetch can resolve. No drawer
  // (cart page type, no section id) = nothing to wait for.
  function waitForDrawerRefresh() {
    var targets = [];
    try {
      if (typeof MutationObserver !== 'function') return null;
      document.querySelectorAll('cart-drawer').forEach(function (drawer) {
        var sectionId = drawer.getAttribute('data-section-id');
        var target = sectionId && document.getElementById('CartDrawer-' + sectionId);
        if (target) targets.push(target);
      });
    } catch (e) {
      return null;
    }
    if (!targets.length) return null;
    return Promise.all(targets.map(function (target) {
      return new Promise(function (resolve) {
        var timer = null;
        var lateTimer = null;
        var timedOut = false;
        var observer = new MutationObserver(function () {
          stop();
          if (timedOut) republishFreshCart();
          else resolve();
        });
        function stop() {
          observer.disconnect();
          clearTimeout(timer);
          clearTimeout(lateTimer);
        }
        observer.observe(target, { childList: true });
        timer = setTimeout(function () {
          // Stop holding the reconcile back, but keep watching for a late
          // replacement (see DRAWER_LATE_WATCH_MS).
          timedOut = true;
          resolve();
          lateTimer = setTimeout(stop, DRAWER_LATE_WATCH_MS);
        }, DRAWER_REFRESH_TIMEOUT_MS);
      });
    }));
  }

  // The drawer's refresh landed after we gave up waiting, so it may show lines
  // our reconcile already removed (with data-index remove links pointing at
  // the wrong line). Re-render from the current cart; the cartUpdate
  // subscriber re-checks it like any other publish.
  function republishFreshCart() {
    enqueue(async function () {
      var cart = await postUpdate({});
      if (!cart) return;
      var next = parseState(cart.sections);
      if (next) setState(next);
      publishCart(cart);
    }).catch(noop);
  }

  /* ================= event wiring ================= */

  function onGroupedSections(event) {
    var ids = event && event.detail && event.detail.sections;
    if (Array.isArray(ids) && ids.indexOf(SECTION_ID) === -1) ids.push(SECTION_ID);
  }

  // Registered before cart.js, so this runs BEFORE CartItems re-renders:
  // never touch cart DOM synchronously in here, and never throw (a throw
  // would abort the publish loop and skip the theme's own subscribers).
  function onCartUpdate(payload) {
    try {
      // A pending notice lives for exactly one cart re-render: the
      // cart:updated events this publish triggers insert it, then it clears.
      if (pendingNotice) {
        var text = pendingNotice;
        setTimeout(function () { if (pendingNotice === text) pendingNotice = null; }, 0);
      }

      var cart = payload && payload.cart;
      if (!cart || cart.errors) return;
      var state = parseState(cart.sections);
      if (!state) return;
      setState(state);
      // ProductForm's own publish is followed by its `finally`, which removes
      // aria-disabled from the button that was just clicked; sync again after it.
      setTimeout(function () { if (lastState) syncAddButtons(lastState.claimed === true); }, 0);
      if (inFlight === 0) scheduleAutoReconcile();
    } catch (error) {
      console.warn(LOG_PREFIX, error);
    }
  }

  // CartItems dispatches cart:updated after each re-render (once per
  // <cart-items>, so this can run several times per publish; it's idempotent).
  function onCartUpdated() {
    // A notice lives for one re-render. Most sit inside the re-rendered markup
    // and vanish with it; one placed outside an emptied cart (below) doesn't,
    // so clear them all here first.
    document.querySelectorAll('[data-giveaway-notice]').forEach(function (node) { node.remove(); });
    if (!pendingNotice) return;
    var text = pendingNotice;
    document.querySelectorAll('cart-items').forEach(function (cartItems) {
      if (!cartItems.parentNode) return;
      var notice = document.createElement('div');
      notice.className = 'giveaway-notice alert alert--success';
      notice.setAttribute('data-giveaway-notice', '');
      notice.textContent = text;
      // When the cart is now empty (e.g. a claimed customer's only line was
      // removed) CartItems hides the wrapper around <cart-items>
      // (#CartDrawerBody-<id>, the cart page form) with .hidden. Insert before
      // that wrapper instead so the reason shows next to the empty state.
      var hidden = typeof cartItems.closest === 'function' ? cartItems.closest('.hidden') : null;
      var anchor = hidden && hidden.parentNode ? hidden : cartItems;
      anchor.parentNode.insertBefore(notice, anchor);
    });
    // Clear once this task ends, so a second re-render in the same publish
    // (another <cart-items>) still gets the notice.
    setTimeout(function () { if (pendingNotice === text) pendingNotice = null; }, 0);
  }

  function onPageLoad() {
    var state = normalizeState(CFG.state);
    if (!state) return;
    setState(state);
    scheduleAutoReconcile();
  }

  // The theme swapped in product cards whose data-giveaway-status came from
  // when that HTML was rendered: facets.js replays cached grid HTML on
  // filter / sort changes (it publishes facetUpdate and dispatches
  // collection:rerendered after the grid DOM is replaced), and quick view
  // loads its content late. Re-apply the last known state. Never throws: it
  // also runs inside pubsub.publish, where a throw would skip the theme's own
  // subscribers.
  function onCardsRendered() {
    try {
      if (lastState) renderStatus(lastState);
    } catch (error) {
      console.warn(LOG_PREFIX, error);
    }
  }

  document.addEventListener('cart:grouped-sections', onGroupedSections);
  FT.pubsub.subscribe(CART_UPDATE, onCartUpdate);
  document.addEventListener('cart:updated', onCartUpdated);
  document.addEventListener('cart:refresh', function () {
    refreshFromServer(waitForDrawerRefresh());
  });
  window.addEventListener('pageshow', function (event) {
    // bfcache restore: no drawer refresh is racing us.
    if (event.persisted) refreshFromServer(null);
  });
  if (FT.pubsub.PUB_SUB_EVENTS.facetUpdate) {
    FT.pubsub.subscribe(FT.pubsub.PUB_SUB_EVENTS.facetUpdate, onCardsRendered);
  }
  document.addEventListener('collection:rerendered', onCardsRendered);
  // Variant change: ProductForm.resetFormState removed aria-disabled from the
  // add button before the new variant rendered; re-apply the claimed state.
  if (FT.pubsub.PUB_SUB_EVENTS.variantChange) {
    FT.pubsub.subscribe(FT.pubsub.PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(onCardsRendered, 0);
    });
  }
  document.addEventListener('quick-view:loaded', onCardsRendered);

  FT.giveaway = {
    afterAdd: afterAdd,
    getState: function () {
      return lastState
        ? {
          claimed: lastState.claimed,
          lines: lastState.lines.map(function (line) { return Object.assign({}, line); }),
        }
        : null;
    },
    _planReconcile: planReconcile,
  };

  // Deferred scripts run while readyState is already 'interactive' but before
  // DOMContentLoaded, so readyState alone can't tell whether it has fired.
  // Listen for both DOMContentLoaded and load; whichever comes first runs.
  var pageLoadRan = false;
  function runPageLoadOnce() {
    if (pageLoadRan) return;
    pageLoadRan = true;
    onPageLoad();
  }
  if (document.readyState === 'complete') {
    runPageLoadOnce();
  } else {
    document.addEventListener('DOMContentLoaded', runPageLoadOnce, { once: true });
    window.addEventListener('load', runPageLoadOnce, { once: true });
  }
})();
